import { defineStore } from "pinia";
import { DEFAULT_USER_AVATAR } from "%/dynamic-component-editor/constants/user-constants";
import { DEFAULT_ORG_LOGO, DEFAULT_SPLASH_IMAGE } from "%/dynamic-component-editor/constants/publication-constants";
import { links } from "%/dynamic-component-editor/enums/link-enums";
import { useSessionStorage } from "@vueuse/core";
import { CAPABILITY_PUBLICATIONS, CAPABILITY_SUB_AFFILIATE_NETWORK } from "@/constants/capability-constants";
import { waitlistStatus } from "@/enums/waitlist-enum";

export const useGeneralStore = defineStore("general-store", {
  state: () => ({
    appId: null,
    currentPath: "",
    orgId: 0,
    userId: 0,
    inspirationUserId: 0,
    inspirationOrgId: 0,
    inspirationUserData: {},
    inspirationUserRoles: {},
    userData: {},
    userRoles: {},
    orgData: {},
    links: [],
    baseDomain: "",
    redirectLocation: null,
    isCustomDomain: false,
    adminUserId: 0,
    adminUserData: {},
    loggingEnabled: useSessionStorage("logging-enabled", false),
    isLoading: false,
    userProfiles: {},
  }),
  getters: {
    inspirationUserEmail() {
      if (this.isLoggedIntoInspiration) {
        return this.userData.email_data.email_address;
      }
      return "";
    },
    isLoggedIntoInspiration() {
      return this.inspirationUserId > 0;
    },
    // Deliberately the same slot as `webUrl`. Both renderers compose `web` from
    // Publication::getPublicationLinkForEnvironmentWithScheme($environment):
    // BaseFanWebController additionally emits it as `fan_url`, but
    // BaseStudioController — which renders the fan-facing DC surfaces
    // (destination / staging / design / build) — only ever sets `web`. Reading
    // `fanUrl` meant those pages resolved to "", so every `${fanUrl}/...` link
    // collapsed to a root-relative path and walked off the publication:
    // `/logout` reached the platform logout instead of the publication's.
    fanUrl() {
      return this.links.web || "";
    },
    webUrl() {
      return this.links.web || "";
    },
    isStaging() {
      return (
        window.location.hostname.includes("staging") ||
        window.location.pathname.includes("/staging")
      );
    },
    isBuildMode() {
      return (
        window.location.hostname.includes("build") ||
        window.location.pathname.startsWith("/build")
      );
    },
    productionFanUrl() {
      if (this.isStaging) {
        return window.location.protocol + "//" + window.location.host + window.location.pathname.replace("/staging", "");
      }
      return this.fanUrl;
    },
    contentApiV1() {
      return useGeneralStore().getLink(links.contentApiV1);
    },
    apiV1() {
      return useGeneralStore().getLink(links.api);
    },
    fanApiV1() {
      return useGeneralStore().getLink(links.fanApiV1);
    },
    livekitServerUrl() {
      return useGeneralStore().getLink(links.livekitUrl);
    },
    isFakeLoggedIn() {
      return this.adminUserId > 0;
    },
    isLoggedIn() {
      return this.userId > 0;
    },
    settingsBreadcrumbs() {
      return [
        { url: "/", label: "Home" },
        { url: `/settings/org/${this.orgId}`, label: "Settings" },
      ];
    },
    getLink: state => linkName => {
      return state.links[linkName];
    },
    userAvatar() {
      if (this.userData.avatar_url) {
        return this.userData.avatar_url;
      }
      return DEFAULT_USER_AVATAR;
    },
    impersonatorAvatar() {
      if (this.adminUserData.avatar_url) {
        return this.adminUserData.avatar_url;
      }
      return DEFAULT_USER_AVATAR;
    },
    userBio() {
      return this.userData.bio;
    },
    impersonatorUsername() {
      if (this.isFakeLoggedIn && this.adminUserData?.first_name) {
        return `${this.adminUserData.first_name} ${this.adminUserData.last_name}`.trim();
      }
      return "";
    },
    username() {
      return `${this.userData.first_name} ${this.userData.last_name}`.trim();
    },
    userSplashImage() {
      if (this.userData.splash_image_url) {
        return this.userData.splash_image_url;
      }
      return DEFAULT_SPLASH_IMAGE;
    },
    orgSplash() {
      if (this.orgData.splash_url) {
        return this.orgData.splash_url;
      }
      return DEFAULT_SPLASH_IMAGE;
    },
    orgLogo() {
      if (this.orgData.logo_url) {
        return this.orgData.logo_url;
      }
      return DEFAULT_ORG_LOGO;
    },
    orgCapabilities() {
      return this.orgData.capabilities;
    },
    isCreator() {
      const hasCreatorCapabilities = this.orgData.capabilities.includes(CAPABILITY_SUB_AFFILIATE_NETWORK)
          && this.orgData.capabilities.includes(CAPABILITY_PUBLICATIONS);
      const isApprovedAsCreator = this.orgData.waitlist_status === waitlistStatus.creatorApproved;
      return isApprovedAsCreator && hasCreatorCapabilities;
    },
  },
  actions: {
    setAppId(id) {
      this.appId = id;
    },
    setCurrentPath(currentPath) {
      this.currentPath = currentPath;
    },
    setLinks(links) {
      this.links = links;
    },
    setUserId(userId) {
      this.userId = parseInt(userId);
    },
    setInspirationUserId(userId) {
      this.inspirationUserId = parseInt(userId);
    },
    setInspirationOrgId(orgId) {
      this.inspirationOrgId = parseInt(orgId);
    },
    setOrgData(data) {
      this.orgData = data;
      this.orgId = parseInt(data.org_id);
    },
    setUserData(data) {
      this.userData = data;
    },
    setBaseDomain(baseDomain) {
      this.baseDomain = baseDomain;
    },
    setRedirectLocation(redirectLocation) {
      this.redirectLocation = redirectLocation;
    },
    setIsCustomDomain(isCustomDomain) {
      this.isCustomDomain = isCustomDomain;
    },
    setIsLoading(isLoading) {
      this.isLoading = isLoading;
    },
    setAdminUserId(adminUserId) {
      this.adminUserId = adminUserId;
    },
    setAdminUserData(data) {
      this.adminUserData = data;
    },
    redirectTo(href, fallbackHref = "/") {
      if (!href && !this.redirectLocation) {
        window.location = this.getRelativeHref(fallbackHref);
      } else if (href) {
        window.location = this.getRelativeHref(href);
      } else {
        window.location = this.getRelativeHref(this.redirectLocation);
      }
    },
    getRedirectUrl(href, fallbackHref = "/") {
      let result = "";
      if (!href && !this.redirectLocation) {
        result = this.getRelativeHref(fallbackHref);
      } else if (href) {
        result = this.getRelativeHref(href);
      } else {
        result = this.getRelativeHref(this.redirectLocation);
      }
      return result;
    },
    getRelativeHref(location, redirectLocation) {
      if (!redirectLocation) {
        redirectLocation = this.redirectLocation;
      }
      if ([this.redirectLocation, redirectLocation].includes(location)) {
        redirectLocation = null;
      }
      let url = `${this.links.web}${location}`;
      url = !redirectLocation ? url : `${url}?redirect=${redirectLocation}`;
      return url;
    },
    cancelFakeLogin() {
      this.apiPost(`${this.links.adminApiV1}/user/login/fake/cancel`, {}, () => {
        window.location.href = useGeneralStore().getLink(links.web);
      });
    },
    saveOnboardingApplication(termsAndConditionsAccepted, excitementText, areaOfInterest, complete) {
      this.apiPost(
        `org/${this.orgId}/apply`,
        {
          terms_and_conditions_accepted: termsAndConditionsAccepted,
          excitement_text: excitementText,
          area_of_interest: areaOfInterest,
        },
        () => {
          this.isLoading = false;
          complete();
        },
        () => this.isLoading = false,
      );
    },
    setUserProfileData(userId) {
      const userProfile = this.userProfiles[userId];

      if (userProfile) {
        this.isLoading = false;
        return;
      }

      this.isLoading = true;

      this.apiGet(`/user/${userId}/profile`, {}, data => {
        this.isLoading = false;
        this.userProfiles = {
          ...this.userProfiles,
          [userId]: data,
        };
      }, () => this.isLoading = false);
    },
  },
});
